import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiArrowLeft, FiArrowRight, FiChevronLeft, FiChevronRight, FiBookOpen } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';

interface Chapter {
  id: number;
  era: string;
  title: string;
  year: string;
  image: string;
  paragraphs: string[];
  highlights?: string[];
  quote?: string;
}

const chapters: Chapter[] = [
  {
    id: 0,
    era: 'The Spark',
    title: 'Halloween Night, 1985',
    year: '1985',
    image: '/images/bio-halloween-pc.png',
    paragraphs: [
      'It was Halloween night, 1985. While every other 10-year-old on the block was out collecting candy, a boy named Glen Rhodes sat transfixed in front of a glowing green monochrome screen. His parents had just brought home an IBM PC — and in that single moment, everything changed.',
      'There would be no trick-or-treating that year. No candy. No costume. Just the hypnotic blink of a cursor and the first lines of BASIC he would ever type. The candy could wait. This was better.',
      'What nobody knew yet was that Glen had been playing piano since he was four years old. Code and music had been running in parallel from the very beginning — two languages for the same creative impulse. That Halloween night just added a second obsession to the first.',
      'That night sparked a passion that would burn for the next four decades — a relentless curiosity about what computers could do, and an even deeper drive to push them further than anyone thought possible. By high school, Glen was president of the computer club, teaching programming to his peers and organizing coding competitions.'
    ],
    quote: '"While other kids collected candy, I collected my first lines of code."'
  },
  {
    id: 1,
    era: 'The Foundation',
    title: 'University of Toronto',
    year: '1994–1998',
    image: '/images/bio-university.png',
    paragraphs: [
      'By the time Glen arrived at the University of Toronto to study Computer Science and Mathematics, he\'d already been coding for nearly a decade. But university would transform raw passion into structured expertise — algorithms, 3D mathematics, data structures, and the theoretical foundations that underpin everything.',
      'It was here that the seeds of a game development career were truly planted. The combination of mathematical rigor and creative thinking would prove to be Glen\'s superpower — the ability to see both the elegant equation and the experience it could create.',
      'Armed with a B.Sc. in Computer Science, Glen was ready to enter the professional world. And he wasted no time.'
    ],
    highlights: ['B.Sc. Computer Science & Mathematics', 'Algorithms & 3D Mathematics', 'Foundation for 25+ year career']
  },
  {
    id: 2,
    era: 'Going Pro',
    title: 'Console Games & PlayStation',
    year: '1995–1998',
    image: '/images/bio-playstation.png',
    paragraphs: [
      'Even before graduating, Glen landed at Gray Matter — a real game studio making real console games. This wasn\'t hobby coding anymore. This was C/C++, Assembly Language, Sony PlayStation dev kits, and Sega Saturn hardware.',
      'The result? "Perfect Weapon" — a published PlayStation title that shipped worldwide. At an age when most people are still figuring out what they want to do, Glen was already a shipped game developer with his name in the credits of a retail console game.',
      'Working with the raw hardware of the original PlayStation — writing code close to the metal, optimizing every cycle — instilled a deep respect for performance and efficiency that would serve him for decades to come.'
    ],
    highlights: ['Published "Perfect Weapon" on PlayStation', 'C/C++ & Assembly Language', 'Sony PlayStation & Sega Saturn development'],
    quote: '"There\'s nothing quite like seeing your game on a store shelf for the first time."'
  },
  {
    id: 3,
    era: 'The Flash Revolution',
    title: 'Web Games, Viral Hits & Published Author',
    year: '2000–2008',
    image: '/images/bio-flash-era.png',
    paragraphs: [
      'The early 2000s saw the web explode — and Glen was right at the center of it. Starting at Parlay Games building casino and lottery game frontends, he quickly became one of the top Flash developers in the world.',
      'In 2001, Glen published his first book: "Flash Games Studio." It was the beginning of a prolific writing career that would span 10+ published titles on game development and interactive media. He became a recognized authority — his books distributed internationally, his expertise sought at conferences worldwide.',
      'Then came 2003. Glen recreated Wolfenstein 3D entirely in Flash — building a complete ray-casting 3D engine inside the browser. It went viral almost overnight — hundreds of thousands of hits per day in an era before YouTube, before social media, before "going viral" was even a phrase. He didn\'t stop there: he also recreated Commander Keen in Flash, and built the Flash Ray Engine — a groundbreaking 3D rendering engine that he licensed to other developers.',
      'This led to a role as Technical Director at CRASH!MEDIA Corp, where Glen led projects for BBC (including a six-screen interactive display at their new UK headquarters), Microsoft, Nike, Toyota, Honda, Discovery Channel, ESPN, and more.'
    ],
    highlights: ['10+ published books on game development', 'Wolfenstein 3D & Commander Keen in Flash — viral sensations', 'Flash Ray Engine — built and licensed a 3D engine', 'Clients: BBC, Microsoft, Toyota, Nike, Honda, ESPN']
  },
  {
    id: 4,
    era: 'Hollywood & Big Brands',
    title: 'Disney, Nike, MTV & More',
    year: '2002–2008',
    image: '/images/bio-big-clients.png',
    paragraphs: [
      'Glen\'s Flash expertise put him in the room with the biggest entertainment brands on the planet. For Disney, he created "Pizza Party Pickup" (based on The Suite Life of Zack and Cody) which became the top-played game on Disney.com. He also built "Jake\'s Dragon" — a Mahjong-style game for Disney Online.',
      'For MTV, he created the Moonman Blast game for the Video Music Awards — a 3D experience where viewers fired Moonman statuettes at their favorite nominees. He designed the SpongeBob board games suite for Nickelodeon and built "Catch The Train" for Nike\'s Nikelab.com, promoting their Shox NZ shoes.',
      'For Honda, he created the Racing Game and online community at honda.ca. He also did the music for "The Black Knight: Get Medieval" for ArcadeTown — one of many projects where his dual skills as programmer and composer came together. These weren\'t small freelance gigs — these were projects for the biggest brands in entertainment, delivered at the highest level.'
    ],
    highlights: ['Disney — top-played game on Disney.com', 'MTV Video Music Awards game', 'Nike, Honda, Nickelodeon SpongeBob', 'Composer + programmer on multiple titles'],
    quote: '"When Disney tells you their game is the most played on their entire site, that\'s a moment you remember."'
  },
  {
    id: 5,
    era: 'On Stage',
    title: 'Conference Speaker & Industry Voice',
    year: '2002–2008',
    image: '/images/bio-conference.png',
    paragraphs: [
      'Glen\'s expertise didn\'t stay confined to his desk. He became a sought-after speaker at the most prestigious Flash and game development conferences in the world — FITC Toronto, FlashForward, FlashOnTheBeach, and FlashKit.',
      'Year after year, he took the stage to share his knowledge: Flash MX Games, real-time multiplayer with Flash Communication Server, game development optimization, and the emerging landscapes of interactive media. He also competed in and defended the IronFlash championship at FITC — a live coding competition judged on creativity and technical skill.',
      'Beyond the stage, Glen wrote articles for Web Designer Magazine, Practical Web Projects, and MX Developer\'s Journal — covering everything from building audio mixers and drum machines in Flash to creating dynamic soundtracks programmatically. As a published author, conference speaker, and magazine contributor, he was one of the foremost voices in interactive entertainment.'
    ],
    highlights: ['FITC, FlashForward, FlashOnTheBeach speaker', 'IronFlash championship competitor & defender', 'Magazine articles: Web Designer, Practical Web Projects']
  },
  {
    id: 6,
    era: 'The Musician',
    title: 'Composing for Oprah & Network Television',
    year: '2005–2011',
    image: '/images/bio-music-oprah.png',
    paragraphs: [
      'Here\'s something most people don\'t expect from a game developer: Glen is also an accomplished orchestral composer. As a composer with Studio 51 Music — an established provider of film and television music — and registered with both ASCAP and SOCAN, he composed music that aired on major network television.',
      'His most notable engagement? Six years composing for The Oprah Winfrey Show — from 2005 until the show\'s final episode in 2011. His music reached millions of viewers on primetime specials including the Oprah Legends Ball and the Martin Luther King 45th anniversary special. His work can still be heard on Dr. Oz, Dr. Phil, and the Oprah 20th Anniversary DVD box set.',
      'Glen has also created orchestral recordings of Les Misérables instrumental tracks that were heard on Britain\'s Got Talent, released an orchestral album "Inward Journey" on Spotify, and spent years gigging as a keys player in a band at venues across Toronto. In 2020, he composed "Who We Are" — a 14-minute choral work for SATB chorus, children\'s choir, and soprano soloist, published by Cypress Choral Music. It\'s a powerful narrative about climate change told through three voices: Humanity, the Children who inherit our future, and Mother Earth.',
      'As a computer scientist, Glen has honed the craft of using technology to create orchestral music nearly indistinguishable from live recordings. This dual life as both a programmer and a musician speaks to something fundamental — he\'s not just a technologist, he\'s a creator.'
    ],
    highlights: ['6 years composing for The Oprah Winfrey Show', '"Who We Are" — published choral symphony (Cypress Choral Music)', 'Les Mis recordings heard on Britain\'s Got Talent', 'Orchestral album "Inward Journey" on Spotify'],
    quote: '"Code and music aren\'t as different as people think. Both are about structure, timing, and creating something that resonates."'
  },
  {
    id: 7,
    era: 'Mobile & Indie',
    title: 'Co-Founding a Game Studio',
    year: '2014–2016',
    image: '/images/bio-mobile-games.png',
    paragraphs: [
      'When the App Store revolution hit, Glen was ready. In 2014, he co-founded Tiny Moosh Games and dove headfirst into mobile game development. Their game "Hopple Hop" hit the Top 25 charts in multiple countries — a genuine indie success story.',
      'Simultaneously, he founded CMR Games and began development on ExtroForge — an ambitious RTS/FPS/Sandbox game set on an alien world, 2000 years in the future. Built in Unreal Engine, it was the kind of passion project that showed what happens when creative vision meets technical mastery.',
      'He also served as Director of Creative Technology at Picture This, developing RE3D — an innovative Unity 3D application that transformed 2D floor plans into fully walkable, decoratable 3D environments for the real estate industry. And at CMR Games, he created "Concerto" — a VR piano simulation in Unreal Engine.',
      'Glen was also pioneering augmented reality before it was mainstream: he built an ARKit app for a children\'s book launch where users walked through a portal into a fantasy storybook world, created an AR employee training app for a major grocery chain (Pokemon GO-style), and developed an AR experience for a multimillion-dollar Manhattan retail store opening.'
    ],
    highlights: ['Hopple Hop — Top 25 in multiple countries', 'ExtroForge — ambitious sci-fi game', 'RE3D — 3D real estate visualization', 'ARKit storybook app & retail AR experiences']
  },
  {
    id: 8,
    era: 'Virtual Reality',
    title: 'Pioneering VR Experiences',
    year: '2016–2021',
    image: '/images/bio-vr-era.png',
    paragraphs: [
      'When virtual reality emerged as the next frontier, Glen didn\'t just participate — he led. As Tech Director at Secret Location, he developed VR games for major brands including Mountain Dew, shipping across Steam, Rift, and Daydream platforms. He even hacked the Unreal Engine source code to enable runtime Python script execution for dynamic VR interactivity.',
      'At Cream Productions, he created a lifelike VR avatar of actor Dominic Monaghan (Lord of the Rings, Lost) — pushing the boundaries of what real-time character rendering could achieve. He managed and mentored a team of junior and intermediate developers.',
      'Then at Dark Slope Studios, he developed location-based free-roam VR games: "ScaryGirl Mission Maybee" and "Slugterra Slugslinger VR" — published for Zero Latency venues worldwide. He also integrated Microsoft\'s Azure Kinect body tracking into Unreal Engine 4 as a C++ plugin for a live venue sports experience. These weren\'t just games; they were experiences where players physically walked through virtual worlds with backpack PCs, motion capture, and full-body tracking.'
    ],
    highlights: ['VR games for Mountain Dew, Zero Latency', 'Lifelike VR avatar of Dominic Monaghan', 'Unreal Engine C++ & source modifications'],
    quote: '"VR is the closest we\'ve come to teleportation. You\'re not just playing — you\'re there."'
  },
  {
    id: 9,
    era: 'AAA & Leadership',
    title: 'Technical Director on a Major Franchise',
    year: '2021–2023',
    image: '/images/bio-aaa-studio.png',
    paragraphs: [
      'In 2023, Glen took on one of the biggest challenges of his career: Technical Director at TLM Partners, leading the technical development of a AAA game set in an esteemed sci-fi franchise universe.',
      'Managing a team of 15 developers, he had full oversight of gameplay, technical design, documentation, and code reviews. He implemented rigorous version control processes in Perforce, facilitated daily scrums, sprint planning, and retrospectives, and played a pivotal role in securing a major deal for the company.',
      'This role cemented Glen\'s reputation as not just a brilliant individual contributor, but a leader who could steer large teams through the complex, high-stakes world of AAA game development.'
    ],
    highlights: ['Led 15-person development team', 'AAA sci-fi franchise title', 'Full technical oversight & code review process']
  },
  {
    id: 10,
    era: 'The AI Revolution',
    title: '50+ AI-Powered Projects',
    year: '2023–Present',
    image: '/images/bio-ai-revolution.png',
    paragraphs: [
      'When the AI revolution arrived, Glen didn\'t just adapt — he detonated. With 25+ years of development expertise as his foundation, he built over 50 innovative projects spanning AI video generation, voice assistants, automated content creation, SaaS platforms, and creative tools.',
      'His portfolio reads like a catalog of what\'s possible when deep technical skill meets creative vision: an AI voice assistant with breakthrough single-step processing, visual scene editors for AI video generators, automated video production systems that create cinema from text, semantic search engines for legal documents, and AI-powered platforms for everything from accounting to dating.',
      'From InfiniteStoryVideoMaker (which uses vector databases for narrative memory) to VidPromptMaker (with OpenAI Sora integration), from AutoRedact (Google Document AI for PII detection) to CivicPlatform (Tinder for civic engagement) — each project demonstrates a restless drive to explore what AI can do in every domain imaginable.',
      'His tech stack spans Python, Flask, React, TypeScript, Node.js, PostgreSQL, MongoDB, OpenAI, Claude, Gemini, ChromaDB, FAISS, and dozens more. He doesn\'t just use AI — he architects systems around it.'
    ],
    highlights: ['50+ innovative AI projects built', 'Full-stack: Python, React, Node.js, TypeScript', 'OpenAI, Claude, Gemini, Sora, ChromaDB, FAISS']
  },
  {
    id: 11,
    era: 'The Author',
    title: '10+ Books & Counting',
    year: '2001–2026',
    image: '/images/bio-books.png',
    paragraphs: [
      'Throughout his career, Glen has been a prolific author. Starting with "Flash Games Studio" in 2001, he went on to publish over 10 books on game development, interactive media, and programming — titles distributed internationally through major publishers.',
      'His published works include Flash Games Studio, Macromedia Flash MX Designer\'s ActionScript Reference, Flash MX Application & Interface Design, Flash Professional 8 Game Development, Unity 2D Game Development, and several more. Each book distilled his hands-on expertise into accessible knowledge for the next generation of developers.',
      'And the writing hasn\'t stopped. In 2026, Glen is releasing his newest book: "AI-Assisted Software Development" — a deep dive into the revolutionary ways artificial intelligence is transforming how we write code. Drawing from his experience building 50+ AI-powered projects, this book bridges the gap between the AI hype and practical, production-ready development.'
    ],
    highlights: ['10+ published books since 2001', 'Flash Games Studio, Unity 2D, ActionScript Reference', 'NEW in 2026: "AI-Assisted Software Development"'],
    quote: '"Every book I write is a snapshot of what\'s possible. The 2026 book is about the biggest shift in software development since the internet."'
  },
  {
    id: 12,
    era: 'What\'s Next',
    title: 'The Journey Continues',
    year: 'Present & Beyond',
    image: '/images/bio-future.png',
    paragraphs: [
      'From a 10-year-old skipping Halloween to program BASIC, to a Technical Director leading AAA game development, to a one-person AI innovation lab building 50+ projects — Glen\'s journey is a testament to what happens when curiosity never sleeps.',
      'Across 27+ years, his work has touched console games, viral web experiences, network television, mobile hits, virtual reality, augmented reality, AAA franchises, and now the cutting edge of artificial intelligence. He\'s composed music for Oprah, published 10+ books, created the top game on Disney.com, spoken at international conferences, built a 3D engine and licensed it, and created software used by people around the world.',
      'The throughline has always been the same: build things that push boundaries. Whether it\'s a PlayStation game in 1995 or an AI-powered video production system in 2025, the drive is identical — take what\'s possible and make it extraordinary.',
      'The cursor is still blinking. The next line of code is waiting. And Glen\'s not done yet.'
    ],
    quote: '"I\'m still that kid on Halloween night. The technology changes, but the excitement never does."'
  }
];

const Bio: React.FC = () => {
  const navigate = useNavigate();
  const [currentChapter, setCurrentChapter] = useState(0);
  const [direction, setDirection] = useState(0);
  const contentRef = useRef<HTMLDivElement>(null);

  const chapter = chapters[currentChapter];
  const isImageLeft = currentChapter % 2 === 0;
  const progress = ((currentChapter + 1) / chapters.length) * 100;

  const goTo = (idx: number) => {
    if (idx < 0 || idx >= chapters.length) return;
    setDirection(idx > currentChapter ? 1 : -1);
    setCurrentChapter(idx);
  };

  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentChapter]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === ' ') {
        e.preventDefault();
        goTo(currentChapter + 1);
      }
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goTo(currentChapter - 1);
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  });

  const slideVariants = {
    enter: (dir: number) => ({ x: dir > 0 ? 600 : -600, opacity: 0 }),
    center: { x: 0, opacity: 1 },
    exit: (dir: number) => ({ x: dir > 0 ? -600 : 600, opacity: 0 })
  };

  const eraColors: Record<string, string> = {
    'The Spark': '#FF6B35',
    'The Foundation': '#50E3C2',
    'Going Pro': '#E94B3C',
    'The Flash Revolution': '#F5A623',
    'Hollywood & Big Brands': '#E94B3C',
    'On Stage': '#BD10E0',
    'The Musician': '#FFD700',
    'Mobile & Indie': '#7ED321',
    'Virtual Reality': '#4A90E2',
    'AAA & Leadership': '#E94B3C',
    'The AI Revolution': '#00D4FF',
    'The Author': '#F5A623',
    'What\'s Next': '#50E3C2'
  };

  const accentColor = eraColors[chapter.era] || '#4A90E2';

  return (
    <div
      aria-label="Glen Rhodes interactive biography"
      style={{
        minHeight: '100vh',
        background: '#0a0a0f',
        color: '#ffffff',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* Ambient background glow */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: `radial-gradient(ellipse at 30% 20%, ${accentColor}15 0%, transparent 60%), radial-gradient(ellipse at 70% 80%, ${accentColor}10 0%, transparent 50%)`,
        transition: 'background 1s ease',
        pointerEvents: 'none',
        zIndex: 0
      }} />

      {/* Top navigation bar */}
      <motion.header
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 100,
          background: 'rgba(10, 10, 15, 0.85)',
          backdropFilter: 'blur(20px)',
          borderBottom: '1px solid rgba(255,255,255,0.08)',
          padding: '1rem 2rem'
        }}
        initial={{ y: -60 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '1rem'
        }}>
          <motion.button
            onClick={() => navigate('/')}
            aria-label="Back to portfolio"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.6rem 1.2rem',
              background: 'rgba(255,255,255,0.08)',
              border: '1px solid rgba(255,255,255,0.15)',
              borderRadius: '25px',
              color: '#fff',
              fontSize: '0.9rem',
              fontWeight: 600,
              cursor: 'pointer',
              backdropFilter: 'blur(10px)'
            }}
            whileHover={{ scale: 1.05, backgroundColor: 'rgba(255,255,255,0.15)' }}
            whileTap={{ scale: 0.95 }}
          >
            <FiArrowLeft size={18} />
            Portfolio
          </motion.button>

          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <FiBookOpen size={20} style={{ color: accentColor }} />
            <h1 style={{
              fontSize: '1.3rem',
              fontWeight: 700,
              margin: 0,
              background: `linear-gradient(135deg, #fff, ${accentColor})`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}>
              The Glen Rhodes Story
            </h1>
          </div>

          <span style={{
            fontSize: '0.8rem',
            color: 'rgba(255,255,255,0.5)',
            fontWeight: 500
          }}>
            Chapter {currentChapter + 1} of {chapters.length}
          </span>
        </div>

        {/* Progress bar */}
        <div style={{
          maxWidth: '1200px',
          margin: '0.75rem auto 0',
          height: '3px',
          background: 'rgba(255,255,255,0.08)',
          borderRadius: '2px',
          overflow: 'hidden'
        }}>
          <motion.div
            style={{
              height: '100%',
              background: `linear-gradient(90deg, ${accentColor}, ${accentColor}aa)`,
              borderRadius: '2px'
            }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.4, ease: 'easeOut' }}
          />
        </div>
      </motion.header>

      {/* Chapter dots navigation */}
      <div
        aria-label="Chapter navigation"
        style={{
          position: 'fixed',
          right: '1.5rem',
          top: '50%',
          transform: 'translateY(-50%)',
          zIndex: 50,
          display: 'flex',
          flexDirection: 'column',
          gap: '0.5rem'
        }}
      >
        {chapters.map((ch, i) => (
          <motion.button
            key={ch.id}
            onClick={() => goTo(i)}
            aria-label={`Go to chapter ${i + 1}: ${ch.title}`}
            title={ch.era}
            style={{
              width: i === currentChapter ? '12px' : '8px',
              height: i === currentChapter ? '12px' : '8px',
              borderRadius: '50%',
              border: 'none',
              background: i === currentChapter ? accentColor : 'rgba(255,255,255,0.25)',
              cursor: 'pointer',
              padding: 0,
              transition: 'all 0.3s ease'
            }}
            whileHover={{ scale: 1.5 }}
          />
        ))}
      </div>

      {/* Main content */}
      <main
        ref={contentRef}
        style={{
          maxWidth: '1100px',
          margin: '0 auto',
          padding: '3rem 2rem 6rem',
          position: 'relative',
          zIndex: 1
        }}
      >
        <AnimatePresence mode="wait" custom={direction}>
          <motion.article
            key={currentChapter}
            custom={direction}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] }}
          >
            {/* Era badge + year */}
            <motion.div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '1rem',
                marginBottom: '1rem',
                flexWrap: 'wrap'
              }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <span style={{
                padding: '0.4rem 1rem',
                background: `${accentColor}25`,
                border: `1px solid ${accentColor}60`,
                borderRadius: '20px',
                color: accentColor,
                fontSize: '0.8rem',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '1.5px'
              }}>
                {chapter.era}
              </span>
              <span style={{
                color: 'rgba(255,255,255,0.4)',
                fontSize: '0.9rem',
                fontWeight: 500
              }}>
                {chapter.year}
              </span>
            </motion.div>

            {/* Chapter title */}
            <motion.h2
              style={{
                fontSize: 'clamp(2rem, 5vw, 3.2rem)',
                fontWeight: 800,
                lineHeight: 1.15,
                margin: '0 0 2.5rem 0',
                color: '#fff'
              }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              {chapter.title}
            </motion.h2>

            {/* Magazine layout: image + text */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 400px), 1fr))',
              gap: '3rem',
              alignItems: 'start',
              direction: isImageLeft ? 'ltr' : 'rtl'
            }}>
              {/* Image */}
              <motion.div
                style={{ direction: 'ltr' }}
                initial={{ opacity: 0, scale: 0.92 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.35, duration: 0.6 }}
              >
                <div style={{
                  position: 'relative',
                  borderRadius: '16px',
                  overflow: 'hidden',
                  boxShadow: `0 20px 60px ${accentColor}20, 0 8px 30px rgba(0,0,0,0.4)`,
                  border: `1px solid ${accentColor}30`
                }}>
                  <img
                    src={chapter.image}
                    alt={chapter.title}
                    loading="eager"
                    style={{
                      width: '100%',
                      height: 'auto',
                      display: 'block',
                      aspectRatio: '16/10',
                      objectFit: 'cover'
                    }}
                  />
                  {/* Image overlay gradient */}
                  <div style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: '40%',
                    background: 'linear-gradient(transparent, rgba(10,10,15,0.6))',
                    pointerEvents: 'none'
                  }} />
                </div>

                {/* Quote below image */}
                {chapter.quote && (
                  <motion.blockquote
                    style={{
                      marginTop: '1.5rem',
                      padding: '1.25rem 1.5rem',
                      borderLeft: `3px solid ${accentColor}`,
                      background: `${accentColor}08`,
                      borderRadius: '0 12px 12px 0',
                      fontStyle: 'italic',
                      fontSize: '1rem',
                      lineHeight: 1.6,
                      color: 'rgba(255,255,255,0.7)'
                    }}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.7 }}
                  >
                    {chapter.quote}
                  </motion.blockquote>
                )}
              </motion.div>

              {/* Text content */}
              <motion.div
                style={{ direction: 'ltr' }}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.6 }}
              >
                {chapter.paragraphs.map((p, i) => (
                  <motion.p
                    key={i}
                    style={{
                      fontSize: '1.08rem',
                      lineHeight: 1.85,
                      color: 'rgba(255,255,255,0.82)',
                      margin: '0 0 1.5rem 0'
                    }}
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 + i * 0.1 }}
                  >
                    {p}
                  </motion.p>
                ))}

                {/* Highlights */}
                {chapter.highlights && (
                  <motion.div
                    style={{
                      marginTop: '1.5rem',
                      padding: '1.5rem',
                      background: 'rgba(255,255,255,0.04)',
                      border: '1px solid rgba(255,255,255,0.08)',
                      borderRadius: '14px'
                    }}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.8 }}
                  >
                    <h4 style={{
                      fontSize: '0.75rem',
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: '1.5px',
                      color: accentColor,
                      margin: '0 0 1rem 0'
                    }}>
                      Key Highlights
                    </h4>
                    {chapter.highlights.map((h, i) => (
                      <div key={i} style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.75rem',
                        marginBottom: '0.6rem'
                      }}>
                        <div style={{
                          width: '6px',
                          height: '6px',
                          borderRadius: '50%',
                          background: accentColor,
                          flexShrink: 0
                        }} />
                        <span style={{
                          fontSize: '0.95rem',
                          color: 'rgba(255,255,255,0.7)'
                        }}>
                          {h}
                        </span>
                      </div>
                    ))}
                  </motion.div>
                )}
              </motion.div>
            </div>
          </motion.article>
        </AnimatePresence>
      </main>

      {/* Bottom navigation */}
      <motion.nav
        aria-label="Chapter navigation controls"
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 100,
          background: 'rgba(10, 10, 15, 0.9)',
          backdropFilter: 'blur(20px)',
          borderTop: '1px solid rgba(255,255,255,0.08)',
          padding: '1rem 2rem'
        }}
        initial={{ y: 60 }}
        animate={{ y: 0 }}
        transition={{ delay: 0.3 }}
      >
        <div style={{
          maxWidth: '1100px',
          margin: '0 auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <motion.button
            onClick={() => goTo(currentChapter - 1)}
            disabled={currentChapter === 0}
            aria-label="Previous chapter"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.7rem 1.3rem',
              background: currentChapter === 0 ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.08)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: '25px',
              color: currentChapter === 0 ? 'rgba(255,255,255,0.2)' : '#fff',
              fontSize: '0.9rem',
              fontWeight: 600,
              cursor: currentChapter === 0 ? 'default' : 'pointer'
            }}
            whileHover={currentChapter > 0 ? { scale: 1.05 } : {}}
            whileTap={currentChapter > 0 ? { scale: 0.95 } : {}}
          >
            <FiChevronLeft size={18} />
            <span className="nav-text">Previous</span>
          </motion.button>

          <div style={{
            textAlign: 'center',
            flex: 1,
            minWidth: 0
          }}>
            <div style={{
              fontSize: '0.7rem',
              textTransform: 'uppercase',
              letterSpacing: '1.5px',
              color: 'rgba(255,255,255,0.35)',
              marginBottom: '0.2rem'
            }}>
              {currentChapter < chapters.length - 1 ? 'Up Next' : 'The End'}
            </div>
            <div style={{
              fontSize: '0.85rem',
              color: 'rgba(255,255,255,0.6)',
              fontWeight: 500,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              padding: '0 1rem'
            }}>
              {currentChapter < chapters.length - 1
                ? chapters[currentChapter + 1].title
                : 'Thanks for reading!'}
            </div>
          </div>

          <motion.button
            onClick={() => goTo(currentChapter + 1)}
            disabled={currentChapter === chapters.length - 1}
            aria-label="Next chapter"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.7rem 1.3rem',
              background: currentChapter === chapters.length - 1
                ? 'rgba(255,255,255,0.03)'
                : `linear-gradient(135deg, ${accentColor}, ${accentColor}cc)`,
              border: 'none',
              borderRadius: '25px',
              color: currentChapter === chapters.length - 1 ? 'rgba(255,255,255,0.2)' : '#fff',
              fontSize: '0.9rem',
              fontWeight: 600,
              cursor: currentChapter === chapters.length - 1 ? 'default' : 'pointer',
              boxShadow: currentChapter < chapters.length - 1
                ? `0 4px 15px ${accentColor}40`
                : 'none'
            }}
            whileHover={currentChapter < chapters.length - 1 ? { scale: 1.05 } : {}}
            whileTap={currentChapter < chapters.length - 1 ? { scale: 0.95 } : {}}
          >
            <span className="nav-text">Next</span>
            <FiChevronRight size={18} />
          </motion.button>
        </div>
      </motion.nav>

      {/* Mobile responsive styles */}
      <style>{`
        @media (max-width: 768px) {
          .nav-text { display: none; }
        }
      `}</style>
    </div>
  );
};

export default Bio;
